import { useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import { useTracking } from '../hooks/useTracking';
import logService, { ActionCategory } from '../services/logging/logService';
import Select from "../components/Select";
import BeatLoader from "react-spinners/BeatLoader";
import { useSelector } from "react-redux";
import { FaDownload } from "react-icons/fa";
import classNames from "classnames";
import { handleLogout } from "../config/axios";
import { DocumentItem, DocumentFilters, OrderBy, SelectOption, BollaDocumentBody, StandardDocumentBody, BaseDocumentBody } from "../types/documents";
import decryptData from "../decrypt";
import { apiFetch } from '../config/fetch';
import { fetchUserInfo, fetchClientsByAgentCodes, hasMultipleCodes, splitAgentCodes, processAgentCodeValue, getAllAgentCodes } from "../services/userService";
import FiltersModalDocumenti from "../components/FiltersModalDocumenti";

const logSearchDirectly = (action: string, description: string, metadata: any) => {
  logService.logAction(ActionCategory.SEARCH, action, description, metadata);
};



export default function Documenti() {
  const formikRef = useRef<any>(null);
  const tracking = useTracking();
  const [loading, setLoading] = useState<boolean>(true);
  const [documentsData, setDocumentsData] = useState<DocumentItem[] | null>(null);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [agentiOptions, setAgentiOptions] = useState<SelectOption[]>([]);
  const [clientiOptions, setClientiOptions] = useState<SelectOption[]>([]);
  const [authorizedAgentIds, setAuthorizedAgentIds] = useState<string[]>([]);
  const [userRole, setUserRole] = useState<string>("");
  const [currentAgentId, setCurrentAgentId] = useState<string>("");
  const theme = useSelector((state: unknown) => (state as any).menu.theme);

  // Helper function to get filters from localStorage
  const getFiltersFromLocalStorage = () => {
    const filters = localStorage.getItem("documentFilters");
    if (filters) return JSON.parse(filters);
    return {};
  };

  // Early initialization of user data from localStorage
  const [userFromStorage] = useState(() => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        // Set initial role from localStorage
        if (user && user.profile === "SUPERVISOR") {
          setUserRole("supervisor");
        }
        // Set initial agent ID
        if (user && user.db2_user_id) {
          setCurrentAgentId(user.db2_user_id);
          setAuthorizedAgentIds([user.db2_user_id]);
        }
        return user;
      }
    } catch (e) {
      console.error("Failed to parse user data from localStorage");
    }
    return null;
  });

  // Download
  const [downloadedFiles, setDownloadedFiles] = useState<Record<string, string>>({});
  const [documentToDownload, setDocumentToDownload] = useState<DocumentItem | null>(null);

  // Stato ordinamento (default data_documento DESC)
  const [localOrderBy, setLocalOrderBy] = useState<OrderBy[]>(() => {
    const savedFilters = getFiltersFromLocalStorage();
    return savedFilters.orderBy || [{ column: "data_calcolata", mode: "DESC" }];
  });

  // Tipi documento
  const documentTypes = [
    { value: "Fattura", label: "Fattura" },
    { value: "Bolla", label: "Bolla" },
    { value: "Nota credito", label: "Nota credito" },
    { value: "Nota debito", label: "Nota debito" },
    { value: "Fattura Accompagnatoria", label: "Fattura Accompagnatoria" },
  ];

  // Helpers per salvare filtri (incluso l'ordinamento) in localStorage
  const saveFiltersToLocalStorage = (filters: {
    selected_date?: string;
    data_da?: string;
    data_a?: string;
    tipo_documento?: SelectOption | null;
    agenti?: SelectOption | null;
    codice_cliente?: SelectOption | null;
    orderBy?: OrderBy[];
  }) => {
    const effectiveOrderBy = filters.orderBy || localOrderBy;
    const filtersToSave = {
      ...filters,
      orderBy: effectiveOrderBy,
      campo_ordinamento: effectiveOrderBy[0]?.column || "data_calcolata",
      tipo_ordinamento: effectiveOrderBy[0]?.mode || "DESC",
    };
    localStorage.setItem("documentFilters", JSON.stringify(filtersToSave));
  };

  // API: lista agenti/clienti
  const fetchDocumentLists = async (): Promise<string[] | undefined> => {
    let authorizedIds: string[] | undefined;
    
    try {
      // Utilizziamo il nostro servizio per recuperare le informazioni utente
      const { user, isSupervisor, agentCodes, agents } = await fetchUserInfo();
      
      if (!user) {
        console.error("Unable to fetch user info");
        return [];
      }
      
      // Set user role
      const role = isSupervisor ? "supervisor" : "agent";
      setUserRole(role);
      
      // Set current agent ID (primo codice se multiplo)
      const currentAgentCode = agentCodes.length > 0 ? agentCodes[0] : "";
      if (currentAgentCode) {
        setCurrentAgentId(currentAgentCode);
      }
      
      // Format agents for the component
      let agentOptions: SelectOption[] = [];
      
      if (isSupervisor && agents.length > 0) {
        // For supervisors: all agents from group
        agentOptions = agents.map((agent) => ({
          value: agent.db2_user_id,
          label: `${agent.first_name} ${agent.last_name}`,
          db2_user_id: agent.db2_user_id,
        }));
      } else if (currentAgentCode) {
        // For regular agents: only their own code
        agentOptions = [
          {
            value: currentAgentCode,
            label: `${user.firstName} ${user.lastName}`,
            db2_user_id: currentAgentCode,
          },
        ];
      }
      
      // Define authorized IDs for API requests
      if (role === "supervisor") {
        // Per i supervisor: flatmap di tutti i codici agente, gestendo quelli separati da virgola
        authorizedIds = agents.flatMap(agent => {
          if (hasMultipleCodes(agent.db2_user_id)) {
            return splitAgentCodes(agent.db2_user_id);
          }
          return [agent.db2_user_id];
        }).filter(Boolean);
      } else {
        // Per gli agenti: usa tutti i codici (potrebbero essere multipli)
        authorizedIds = agentCodes;
      }
      
      setAuthorizedAgentIds(authorizedIds || []);
      setAgentiOptions(agentOptions);
      
      console.log("User role:", role);
      console.log("Current agent ID:", currentAgentCode);
      console.log("Authorized agent IDs:", authorizedIds);
      console.log("Agent options:", agentOptions);
    } catch (error) {
      console.error("Error fetching agents from keycloak:", error);
      
      // Fallback to localStorage if keycloak call fails
      if (userFromStorage) {
        const isSupervisor = userFromStorage.profile === "SUPERVISOR";
        const agentId = userFromStorage.db2_user_id;
        
        if (agentId) {
          if (!isSupervisor) {
            // For agents, just use their own ID
            authorizedIds = [agentId];
            setAuthorizedAgentIds(authorizedIds);
            setCurrentAgentId(agentId);
            setUserRole("agent");
          } else {
            // For supervisors, we'll have to fetch the agent list separately
            // For now, just use their own ID as fallback
            authorizedIds = [agentId];
            setAuthorizedAgentIds(authorizedIds);
            setCurrentAgentId(agentId);
            setUserRole("supervisor");
          }
        }
      }
    }
  
    // Seconda chiamata: recupero dati per i clienti usando il nostro servizio
    try {
      console.log("Agenti per la ricerca clienti:", authorizedIds);
      
      // Se non abbiamo ID agente, non possiamo procedere
      if (!authorizedIds || authorizedIds.length === 0) {
        console.warn("No agent IDs available for client search");
        return [];
      }
      
      // Utilizziamo il nostro servizio per recuperare i clienti
      const clients = await fetchClientsByAgentCodes(authorizedIds);
      
      if (Array.isArray(clients)) {
        const clientiOpts = clients.map((cliente: any) => ({
          value: cliente.CODICE_CLIENTE,
          label: `${cliente.CODICE_CLIENTE} - ${cliente.RAGIONE_SOCIALE}`,
        }));
        setClientiOptions(clientiOpts);
      }
    } catch (error) {
      console.error("Error fetching document lists for customers:", error);
    }
  
    return authorizedIds;
  };
  
  

  // API: documenti
  const fetchDocuments = async (filters: DocumentFilters = {}, agentIds: string[] = []) => {
    try {
      setLoading(true);
      
      // Converte YYYY-MM-DD → YYYYMMDD
      const toCompactDate = (d?: string) => d ? d.replace(/-/g, "") : undefined;

      // Ensure we always have agent IDs
      let effectiveAgentIds = agentIds;
      
      // If no agent IDs provided, use authorized agent IDs
      if (!effectiveAgentIds || effectiveAgentIds.length === 0) {
        effectiveAgentIds = authorizedAgentIds;
      }
      
      // In case authorized agent IDs are also empty (e.g., during initial load)
      if (!effectiveAgentIds || effectiveAgentIds.length === 0) {
        // Final fallback: check if we have current agent ID from localStorage
        if (currentAgentId) {
          effectiveAgentIds = [currentAgentId];
        } else if (userFromStorage?.db2_user_id) {
          effectiveAgentIds = [userFromStorage.db2_user_id];
        } else {
          console.warn("No agent IDs available for request!");
          effectiveAgentIds = [];
        }
      }
      
      // No need to override effectiveAgentIds for agents
      // They should use all their authorized codes
      
      console.log("Effective agent IDs for request:", effectiveAgentIds);

      const response = await apiFetch("/nodeapi/documenti", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          page: filters.page || 1,
          pageSize: 100,
          agenti: filters.agenti?.length ? filters.agenti : effectiveAgentIds,
          ...(filters.codice_cliente?.length && { codice_cliente: filters.codice_cliente }),
          ...(filters.tipo_documento?.length && { tipo_documento: filters.tipo_documento }),
          campo_ordinamento: filters.campo_ordinamento || localOrderBy[0]?.column || "data_calcolata",
          tipo_ordinamento: filters.tipo_ordinamento || localOrderBy[0]?.mode || "DESC",
          ...(filters.selected_date && { selected_date: filters.selected_date }),
          ...(filters.data_da && { data_da: toCompactDate(filters.data_da) }),
          ...(filters.data_a && { data_a: toCompactDate(filters.data_a) }),
        }),
      });

       if (response.status !== 200 && response.status !== 201) {
          handleLogout(response, "/nodeapi/documenti", JSON.stringify({
          page: filters.page || 1,
          pageSize: 100,
          agenti: filters.agenti?.length ? filters.agenti : effectiveAgentIds,
          ...(filters.codice_cliente?.length && { codice_cliente: filters.codice_cliente }),
          ...(filters.tipo_documento?.length && { tipo_documento: filters.tipo_documento }),
          campo_ordinamento: filters.campo_ordinamento || localOrderBy[0]?.column || "data_calcolata",
          tipo_ordinamento: filters.tipo_ordinamento || localOrderBy[0]?.mode || "DESC",
          ...(filters.selected_date && { selected_date: filters.selected_date }),
          ...(filters.data_da && { data_da: toCompactDate(filters.data_da) }),
          ...(filters.data_a && { data_a: toCompactDate(filters.data_a) }),
        }));
         return;
       }

      const result = await response.json();
      const decrypted = await decryptData(
        result.encryptedData,
        result.encryptedKey,
        result.iv
      );
      const decryptedJson = JSON.parse(decrypted);
      if (decryptedJson.status === "processed") {
        setDocumentsData(decryptedJson.queryResult);
        setTotalPages(decryptedJson.pagination.totalPages);
        setPage(decryptedJson.pagination.currentPage);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setLoading(false);
    }
  };

  // Download singolo documento
  const handleDownload = async (document: DocumentItem): Promise<void> => {
    setDocumentToDownload(document);
  };

  const fetchFile = async (document: DocumentItem): Promise<string | null> => {
    try {
      const baseBody: BaseDocumentBody = {
        cartella: document.CARTELLA,
        registro: document.REGISTRO,
        bollettario: document.BOLLETTARIO,
      };
      
      let requestBody: BollaDocumentBody | StandardDocumentBody;
      
      if (document.TIPO_DOCUMENTO === "Bolla") {
        // For Bolla documents, use numero_bolla and data_bolla
        requestBody = {
          ...baseBody,
          anno: document.ANNO_BOLLA.toString(),
          numero_documento: document.NUMERO_BOLLA.toString().padStart(6, "0"),
          data_documento: document.DATA_BOLLA.toString(),
        };
      } else {
        // For all other document types, use numero_documento and data_documento
        requestBody = {
          ...baseBody,
          anno: document.ANNO_DOCUMENTO.toString(),
          numero_documento: document.NUMERO_DOCUMENTO.toString().padStart(6, "0"),
          data_documento: document.DATA_DOCUMENTO.toString(),
        };
      }
  
      const response = await apiFetch("/nodeapi/files", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(requestBody),
      });
  
      if (response.status !== 200 && response.status !== 201) {
          handleLogout(response, "/nodeapi/files", JSON.stringify(requestBody));
         return;
       }
  
      const result = await response.json();
      const decrypted = await decryptData(
        result.encryptedData,
        result.encryptedKey,
        result.iv
      );
      const decryptedJson = JSON.parse(decrypted);
      if (decryptedJson.status === "processed" && decryptedJson.queryResult.fileContent) {
        return decryptedJson.queryResult.fileContent;
      } else {
        console.error("Errore: fileContent non valido o vuoto.");
        return null;
      }
    } catch (error) {
      console.error("Errore durante il fetch del file:", error);
      return null;
    }
  };

  // Helper function to detect mobile devices
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (window.matchMedia && window.matchMedia("(max-width: 768px)").matches);
  };

  // Helper function to download file with mobile compatibility
  const downloadFileWithMobileSupport = (base64Data: string, fileName: string) => {
    try {
      // Convert base64 to blob
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      // Create blob URL
      const blobUrl = URL.createObjectURL(blob);
      
      if (isMobileDevice()) {
        // For mobile devices, use window.open which is more reliable
        const newWindow = window.open(blobUrl, '_blank');
        if (!newWindow) {
          // If popup blocked, fallback to creating a link
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName;
          link.style.display = 'none';
          document.body.appendChild(link);
          
          // Dispatch a click event instead of calling click()
          const clickEvent = new MouseEvent('click', {
            view: window,
            bubbles: true,
            cancelable: true
          });
          link.dispatchEvent(clickEvent);
          
          // Clean up after a delay
          setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
          }, 100);
        } else {
          // Clean up blob URL after a delay
          setTimeout(() => {
            URL.revokeObjectURL(blobUrl);
          }, 1000);
        }
      } else {
        // Desktop approach
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
        }, 100);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      // Fallback to data URL approach
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${base64Data}`;
      link.download = fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Effetto: download del file
  useEffect(() => {
    if (documentToDownload) {
      const downloadFile = async () => {
        const fileKey = `${documentToDownload.CARTELLA}_${documentToDownload.REGISTRO}_${
          documentToDownload.NUMERO_DOCUMENTO || documentToDownload.NUMERO_BOLLA
        }`;
        const fileName = `${fileKey}.pdf`;

        try {
          // Se già scaricato, apri
          if (downloadedFiles[fileKey]) {
            downloadFileWithMobileSupport(downloadedFiles[fileKey], fileName);
            return;
          }

          // Altrimenti fetch
          const fileContent = await fetchFile(documentToDownload);
          if (fileContent) {
            setDownloadedFiles((prev) => ({ ...prev, [fileKey]: fileContent }));
            downloadFileWithMobileSupport(fileContent, fileName);
          } else {
            console.error("Errore: fileContent non valido o vuoto.");
          }
        } catch (error) {
          console.error("Error in download process:", error);
        }
      };

      downloadFile();
      // Reset documentToDownload after a small delay to ensure the download process starts
      setTimeout(() => {
        setDocumentToDownload(null);
      }, 100);
    }
  }, [documentToDownload, downloadedFiles]);

  // Al mount: carica liste e filtri salvati
  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(true);
        // Fetch agent lists first
        const authorizedIds = await fetchDocumentLists();
        
        // Get saved filters
        const savedFilters = getFiltersFromLocalStorage();
        
        // Check if we have significant filters that should trigger a search
        const hasSignificantFilters = !!(savedFilters.selected_date || 
                                       savedFilters.tipo_documento || 
                                       savedFilters.agenti || 
                                       savedFilters.codice_cliente);
        
        console.log("Saved filters:", savedFilters);
        console.log("Has significant filters:", hasSignificantFilters);
        
        if (authorizedIds && authorizedIds.length > 0) {
          // If we have significant filters, build the full filters object
          if (hasSignificantFilters) {
            let documentTypes: string[] = [];
            if (savedFilters.tipo_documento) {
              if (savedFilters.tipo_documento.value === "Fattura") {
                documentTypes = ["Fattura", "Fattura da Bolla"];
              } else {
                documentTypes = [savedFilters.tipo_documento.value];
              }
            }
            
            const effectiveAgentIds = getEffectiveAgentIds(savedFilters.agenti);
            
            const fullFilters = {
              page: 1,
              agenti: savedFilters.agenti ? 
                (hasMultipleCodes(savedFilters.agenti.value) ? 
                  splitAgentCodes(savedFilters.agenti.value) : 
                  [savedFilters.agenti.value]) : 
                effectiveAgentIds,
              codice_cliente: savedFilters.codice_cliente ? [savedFilters.codice_cliente.value] : [],
              tipo_documento: documentTypes,
              selected_date: savedFilters.selected_date || undefined,
            };
            
            await fetchDocuments(fullFilters, authorizedIds);
          } else {
            // No significant filters, just load all documents
            await fetchDocuments({}, authorizedIds);
          }
        } else {
          // If no authorized IDs available, try with current agent ID from state or localStorage
          const effectiveAgentId = currentAgentId || userFromStorage?.db2_user_id;
          if (effectiveAgentId) {
            if (hasSignificantFilters) {
              let documentTypes: string[] = [];
              if (savedFilters.tipo_documento) {
                if (savedFilters.tipo_documento.value === "Fattura") {
                  documentTypes = ["Fattura", "Fattura da Bolla"];
                } else {
                  documentTypes = [savedFilters.tipo_documento.value];
                }
              }
              
              const fullFilters = {
                page: 1,
                agenti: [effectiveAgentId],
                codice_cliente: savedFilters.codice_cliente ? [savedFilters.codice_cliente.value] : [],
                tipo_documento: documentTypes,
                selected_date: savedFilters.selected_date || undefined,
              };
              
              await fetchDocuments(fullFilters, [effectiveAgentId]);
            } else {
              await fetchDocuments({}, [effectiveAgentId]);
            }
          } else {
            console.error("No authorized agent IDs available");
          }
        }
      } finally {
        setLoading(false);
      }
    };
    initializeData();
  }, []);

  // Gestione Ordinamento stile "BackorderFiltersModal"
  const handleLocalOrderByChange = (column: string) => {
    // Track order change
    tracking.trackFilter(
      "documenti_order_change",
      {
        column,
        currentMode: localOrderBy.length > 0 && localOrderBy[0].column === column ? localOrderBy[0].mode : null,
        newMode: localOrderBy.length > 0 && localOrderBy[0].column === column ? 
          (localOrderBy[0].mode === "ASC" ? "DESC" : "ASC") : "ASC"
      },
      {
        userRole,
        timestamp: new Date().toISOString()
      }
    );
    
    if (localOrderBy.length > 0 && localOrderBy[0].column === column) {
      setLocalOrderBy([
        { column, mode: localOrderBy[0].mode === "ASC" ? "DESC" : "ASC" },
      ]);
    } else {
      setLocalOrderBy([{ column, mode: "ASC" }]);
    }
  };

  // Get agent list for API calls based on role and selection
  const getEffectiveAgentIds = (selectedAgent: SelectOption | null): string[] => {
    const isSupervisor = userRole === "supervisor" || userFromStorage?.profile === "SUPERVISOR";
    
    if (isSupervisor) {
      // For supervisors:
      if (selectedAgent) {
        // If agent is selected, check if it has multiple codes
        if (hasMultipleCodes(selectedAgent.value)) {
          return splitAgentCodes(selectedAgent.value);
        } else {
          return [selectedAgent.value];
        }
      } else {
        // Otherwise, use all authorized agents
        return authorizedAgentIds.length > 0 ? 
          authorizedAgentIds : 
          (currentAgentId ? [currentAgentId] : []);
      }
    } else {
      // For agents: always use all their authorized codes (which might be multiple)
      // authorizedAgentIds contains all codes for the agent, not just the first one
      if (authorizedAgentIds.length > 0) {
        return authorizedAgentIds;
      } else if (currentAgentId && hasMultipleCodes(currentAgentId)) {
        return splitAgentCodes(currentAgentId);
      } else if (currentAgentId) {
        return [currentAgentId];
      } else if (userFromStorage?.db2_user_id) {
        if (hasMultipleCodes(userFromStorage.db2_user_id)) {
          return splitAgentCodes(userFromStorage.db2_user_id);
        } else {
          return [userFromStorage.db2_user_id];
        }
      }
      return [];
    }
  };

  // Cerca
  const handleSearch = async () => {
    const { agenti, codice_cliente, tipo_documento, selected_date, data_da, data_a, campo_ordinamento, tipo_ordinamento } = formikRef.current.values;
    // Calcola ordinamento effettivo dai valori formik (evita race condition con setLocalOrderBy)
    const effectiveOrdinamento = campo_ordinamento || localOrderBy[0]?.column || "data_calcolata";
    const effectiveTipoOrdinamento = tipo_ordinamento || localOrderBy[0]?.mode || "DESC";
    // Aggiorna anche lo stato per sincronizzare future chiamate (es. cambio pagina)
    setLocalOrderBy([{ column: effectiveOrdinamento, mode: effectiveTipoOrdinamento as "ASC" | "DESC" }]);

    saveFiltersToLocalStorage({ selected_date, data_da, data_a, tipo_documento, agenti, codice_cliente, orderBy: [{ column: effectiveOrdinamento, mode: effectiveTipoOrdinamento as "ASC" | "DESC" }] });

    let documentTypes: string[] = [];
    if (tipo_documento) {
      if (tipo_documento.value === "Fattura") {
        documentTypes = ["Fattura", "Fattura da Bolla"];
      } else {
        documentTypes = [tipo_documento.value];
      }
    }

    // Get appropriate agent IDs based on role and selection
    const effectiveAgentIds = getEffectiveAgentIds(agenti);

    // 1. Usa il sistema standard di tracking (potrebbe essere soggetto a debounce)
    tracking.trackSearch('Ricerca Documenti ' + new Date().getTime(), undefined, {
      filters: {
        agenti: agenti ? { value: agenti.value, label: agenti.label } : null,
        codice_cliente: codice_cliente ? { value: codice_cliente.value, label: codice_cliente.label } : null,
        tipo_documento: tipo_documento ? { value: tipo_documento.value, label: tipo_documento.label } : null,
        selected_date: selected_date || undefined,
        orderBy: [{ column: effectiveOrdinamento, mode: effectiveTipoOrdinamento }]
      },
      userRole,
      timestamp: new Date().toISOString()
    });

    // 2. Usa anche il metodo diretto che bypassa il debounce
    logSearchDirectly('documenti_search', 'Ricerca Documenti (Direct Log)', {
      filters: {
        agenti: agenti ? { value: agenti.value, label: agenti.label } : null,
        codice_cliente: codice_cliente ? { value: codice_cliente.value, label: codice_cliente.label } : null,
        tipo_documento: tipo_documento ? { value: tipo_documento.value, label: tipo_documento.label } : null,
        selected_date: selected_date || undefined,
        orderBy: [{ column: effectiveOrdinamento, mode: effectiveTipoOrdinamento }]
      },
      userRole,
      timestamp: new Date().toISOString()
    });

    await fetchDocuments(
      {
        page: 1,
        agenti: agenti ? (hasMultipleCodes(agenti.value) ? splitAgentCodes(agenti.value) : [agenti.value]) : effectiveAgentIds,
        codice_cliente: codice_cliente ? [codice_cliente.value] : [],
        tipo_documento: documentTypes,
        selected_date: selected_date || undefined,
        data_da: data_da || undefined,
        data_a: data_a || undefined,
        campo_ordinamento: effectiveOrdinamento,
        tipo_ordinamento: effectiveTipoOrdinamento,
      },
      authorizedAgentIds
    );
  };

  // Cambio pagina
  const handlePageChange = async (newPage: number) => {
    const { agenti, codice_cliente, tipo_documento, selected_date, data_da, data_a } = formikRef.current.values;

    let documentTypes: string[] = [];
    if (tipo_documento) {
      if (tipo_documento.value === "Fattura") {
        documentTypes = ["Fattura", "Fattura da Bolla"];
      } else {
        documentTypes = [tipo_documento.value];
      }
    }

    // Get appropriate agent IDs based on role and selection
    const effectiveAgentIds = getEffectiveAgentIds(agenti);
    
    // Log page change action
    logSearchDirectly('documenti_page_change', 'Cambio Pagina Documenti (Direct Log)', {
      filters: {
        agenti: agenti ? { value: agenti.value, label: agenti.label } : null,
        codice_cliente: codice_cliente ? { value: codice_cliente.value, label: codice_cliente.label } : null,
        tipo_documento: tipo_documento ? { value: tipo_documento.value, label: tipo_documento.label } : null,
        selected_date: selected_date || undefined,
        orderBy: localOrderBy,
        page: newPage
      },
      userRole,
      timestamp: new Date().toISOString()
    });

    await fetchDocuments(
      {
        page: newPage,
        agenti: agenti ? (hasMultipleCodes(agenti.value) ? splitAgentCodes(agenti.value) : [agenti.value]) : effectiveAgentIds,
        codice_cliente: codice_cliente ? [codice_cliente.value] : [],
        tipo_documento: documentTypes,
        selected_date: selected_date || undefined,
        data_da: data_da || undefined,
        data_a: data_a || undefined,
      },
      authorizedAgentIds
    );
  };

  // Reset filtri
  const clearFilters = (setFieldValue: (field: string, value: any) => void) => {
    // 1. Log clear filters action with standard system
/*     tracking.logAction(
      ActionCategory.USER_ACTION,
      "clear_filters",
      "User cleared all filters in Documenti",
      {
        userRole,
        timestamp: new Date().toISOString()
      }
    ); */
    
    // 2. Log clear filters with direct method
    logSearchDirectly('documenti_clear_filters', 'Reset Filtri Documenti (Direct Log)', {
      userRole,
      timestamp: new Date().toISOString()
    });
    
    setFieldValue("agenti", null);
    setFieldValue("codice_cliente", null);
    setFieldValue("tipo_documento", null);
    setFieldValue("selected_date", "");
    setFieldValue("data_da", "");
    setFieldValue("data_a", "");
    // Resetta l'ordinamento di default
    setLocalOrderBy([{ column: "data_calcolata", mode: "DESC" }]);

    saveFiltersToLocalStorage({ orderBy: [{ column: "data_calcolata", mode: "DESC" }] });
    
    // Get agent IDs based on role (for clean search)
    const effectiveAgentIds = userRole === "supervisor" || userFromStorage?.profile === "SUPERVISOR"
      ? authorizedAgentIds 
      : authorizedAgentIds; // For agents, use all their authorized codes
      
    fetchDocuments({}, effectiveAgentIds);
  };

  // Formatta data numerica AAAAMMGG -> DD/MM/YYYY
  const formatDate = (dateNumber: number): string => {
    if (!dateNumber) return "";
    const dateStr = dateNumber.toString();
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    return `${day}/${month}/${year}`;
  };

  // Paginazione
  const Pagination = () => {
    const maxVisiblePages = 3;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    return (
      <div className="flex justify-center items-center mt-4 mb-8">
        <button
          onClick={() => handlePageChange(1)}
          disabled={page === 1}
          className={`px-3 py-1 mx-1 ${
            page === 1
              ? "bg-gray-400"
              : theme === "dark"
              ? "bg-gray-700 text-white"
              : "bg-gray-200 hover:bg-gray-300"
          } disabled:opacity-50`}
        >
          ««
        </button>

        <button
          onClick={() => handlePageChange(page - 1)}
          disabled={page === 1}
          className={`px-3 py-1 mx-1 ${
            page === 1
              ? "bg-gray-400"
              : theme === "dark"
              ? "bg-gray-700 text-white"
              : "bg-gray-200 hover:bg-gray-300"
          } disabled:opacity-50`}
        >
          «
        </button>

        {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(
          (pageNum) => (
            <button
              key={pageNum}
              onClick={() => handlePageChange(pageNum)}
              className={`px-3 py-1 mx-1 ${
                page === pageNum
                  ? "bg-blue-600 text-white"
                  : theme === "dark"
                  ? "bg-gray-700 text-white hover:bg-gray-600"
                  : "bg-gray-200 hover:bg-gray-300"
              }`}
            >
              {pageNum}
            </button>
          )
        )}

        <button
          onClick={() => handlePageChange(page + 1)}
          disabled={page === totalPages}
          className={`px-3 py-1 mx-1 ${
            page === totalPages
              ? "bg-gray-400"
              : theme === "dark"
              ? "bg-gray-700 text-white"
              : "bg-gray-200 hover:bg-gray-300"
          } disabled:opacity-50`}
        >
          »
        </button>

        <button
          onClick={() => handlePageChange(totalPages)}
          disabled={page === totalPages}
          className={`px-3 py-1 mx-1 ${
            page === totalPages
              ? "bg-gray-400"
              : theme === "dark"
              ? "bg-gray-700 text-white"
              : "bg-gray-200 hover:bg-gray-300"
          } disabled:opacity-50`}
        >
          »»
        </button>
      </div>
    );
  };

  return (
    <Formik
      innerRef={formikRef}
      enableReinitialize={true}
      initialValues={{
        agenti: null,
        codice_cliente: null,
        tipo_documento: null,
        selected_date: "",
        data_da: "",
        data_a: "",
        campo_ordinamento: "data_calcolata",
        tipo_ordinamento: "DESC",
        ...getFiltersFromLocalStorage(),
      }}
      onSubmit={() => {}}
    >
      {({ setFieldValue, values }) => (
        <Form>
          {/* Contenitore principale centrato */}
          <div className="w-full flex-col items-center py-8 pb-24 px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="flex justify-center items-center h-screen">
                <BeatLoader color="#2563eb" />
              </div>
            ) : (
              <>
            <div className="space-y-8 px-5 sm:px-12 mx-auto flex flex-col justify-between mb-8 mt-[50px] md:mt-50">
                <h2 className="hidden sm:block text-4xl text-blue-500 sm:max-w-72 font-bold sm:mb-24">
                  Documenti
                </h2>

                
                <div className="flex flex-row justify-center !mb-20 gap-6">
                  <a
                    className="flex flex-col items-center gap-2 group"
                    href="https://drive.google.com/drive/folders/1DQ8JPfaBwZriaL8D-b32Uul0BBzhsFNM"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="w-14 h-14 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center group-hover:bg-gray-200 dark:group-hover:bg-gray-600 transition-colors">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 87.3 78" className="w-7 h-7">
                        <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/>
                        <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0-1.2 4.5h27.5z" fill="#00ac47"/>
                        <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.5l5.85 11.5z" fill="#ea4335"/>
                        <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/>
                        <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/>
                        <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/>
                      </svg>
                    </div>
                    <span className="text-sm text-gray-700 dark:text-gray-300 text-center">Listini & Cataloghi</span>
                  </a>
                  <a
                    className="flex flex-col items-center gap-2 group"
                    href="https://drive.google.com/drive/folders/1xBY9eveowYkphqZr6SvmgtY1hMk6O61i"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="w-14 h-14 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center group-hover:bg-gray-200 dark:group-hover:bg-gray-600 transition-colors">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 87.3 78" className="w-7 h-7">
                        <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/>
                        <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0-1.2 4.5h27.5z" fill="#00ac47"/>
                        <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.5l5.85 11.5z" fill="#ea4335"/>
                        <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/>
                        <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/>
                        <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/>
                      </svg>
                    </div>
                    <span className="text-sm text-gray-700 dark:text-gray-300 text-center">Modulistica Agenti</span>
                  </a>
                </div>

                {/* Riga 1: tre Select affiancati, stessa larghezza */}
                <div className="flex flex-col md:flex-row md:items-center justify-center md:space-x-4 space-y-4 md:space-y-0 w-full">
                  <div className="cu-wi-se-3 min-h-c border-neutral-300 dark:border-neutral-400 w-full md:w-64">
                    <Select
                      options={documentTypes}
                      placeholder="Tipo Documento"
                      classNames={{
                        container: () => "w-full",
                        control: () => "min-h-c border border-neutral-300 dark:border-neutral-400",
                        input: () => "!text-inherit",
                        singleValue: () => "text-neutral-900 dark:text-neutral-100",
                      }}
                      value={values.tipo_documento}
                      onChange={(val) => setFieldValue("tipo_documento", val)}
                      isClearable
                    />
                  </div>

                  {(userRole === "supervisor" || userFromStorage?.profile === "SUPERVISOR") && (
                    <div className="cu-wi-se-3 min-h-c border-neutral-300 dark:border-neutral-400 w-full md:w-64">
                      <Select
                        options={agentiOptions}
                        placeholder="Seleziona Agente"
                        classNames={{
                          container: () => "w-full",
                          control: () =>
                            "min-h-c border border-neutral-300 dark:border-neutral-400",
                          input: () => "!text-inherit",
                          singleValue: () => "text-neutral-900 dark:text-neutral-100",
                        }}
                        value={values.agenti}
                        onChange={async (val) => {
                          setFieldValue("agenti", val);
                          setFieldValue("codice_cliente", null);
                          const agentCodes = val
                            ? processAgentCodeValue(val.value)
                            : await getAllAgentCodes();
                          if (agentCodes.length > 0) {
                            const clients = await fetchClientsByAgentCodes(agentCodes);
                            if (Array.isArray(clients)) {
                              setClientiOptions(clients.map((cliente: any) => ({
                                value: cliente.CODICE_CLIENTE,
                                label: `${cliente.CODICE_CLIENTE} - ${cliente.RAGIONE_SOCIALE}`,
                              })));
                            }
                          }
                        }}
                        isClearable
                      />
                    </div>
                  )}

                  <div className="cu-wi-se-3 min-h-c border-neutral-300 dark:border-neutral-400 w-full md:w-64">
                    <Select
                      options={clientiOptions}
                      placeholder="Codice Cliente"
                      classNames={{
                        container: () => "w-full",
                        control: () =>
                          "min-h-c border border-neutral-300 dark:border-neutral-400",
                        input: () => "!text-inherit",
                        singleValue: () => "text-neutral-900 dark:text-neutral-100",
                      }}
                      value={values.codice_cliente}
                      onChange={(val) => setFieldValue("codice_cliente", val)}
                      isClearable
                    />
                  </div>
                </div>

                {/* Riga 2: Filtri Avanzati + Bottoni */}
                <div className="flex flex-row items-center justify-center w-full mt-6 gap-4 flex-wrap">
                  <FiltersModalDocumenti
                    values={values}
                    setFieldValue={setFieldValue}

                    saveFilters={(f) => saveFiltersToLocalStorage({
                      selected_date: f.selected_date,
                      data_da: f.data_da,
                      data_a: f.data_a,
                      orderBy: [{ column: f.campo_ordinamento, mode: f.tipo_ordinamento }],
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => clearFilters(setFieldValue)}
                    className="min-h-c py-2 px-4 bg-blue-600 text-white font-semibold hover:bg-blue-700"
                  >
                    Cancella filtri
                  </button>
                  <button
                    type="button"
                    onClick={handleSearch}
                    className="min-h-c py-2 px-4 bg-blue-600 text-white font-semibold hover:bg-blue-700"
                  >
                    Cerca
                  </button>
                </div>

                {/* Tabella */}
                {documentsData && (
                  <div className="w-full mt-8">
                    <div className="relative table-container custom-scrollbar overflow-x-auto mx-auto">
                      <table className="w-full min-w-max text-left border-collapse">
                        <thead>
                          <tr>
                            <th className="pb-2">Tipo Documento</th>
                            <th className="pb-2">Numero</th>
                            <th className="pb-2">Data</th>
                            <th className="pb-2">Numero Bolla</th>
                            <th className="pb-2">Data Bolla</th>
                            <th className="pb-2">Codice Agente</th>
                            <th className="pb-2">Codice Cliente</th>
                            <th className="pb-2">Ragione Sociale</th>
                            <th className="pb-2">Totale</th>
                            <th className="pb-2">Download</th>
                          </tr>
                        </thead>
                        <tbody>
                          {documentsData.map((item, index) => (
                            <tr key={index} >
                              <td className="py-2">{item.TIPO_DOCUMENTO}</td>
                              <td className="py-2">{item.NUMERO_DOCUMENTO}</td>
                              <td className="py-2">{formatDate(item.DATA_DOCUMENTO)}</td>
                              <td className="py-2">{item.NUMERO_BOLLA}</td>
                              <td className="py-2">{formatDate(item.DATA_BOLLA)}</td>
                              <td className="py-2">{item.CODICE_AGENTE}</td>
                              <td className="py-2">{item.CODICE_CLIENTE_FATTURAZIONE}</td>
                              <td className="py-2">{item.RAGIONE_SOCIALE.trim()}</td>
                              <td className="py-2">€ {item.TOTALE_DOCUMENTO.toFixed(2)}</td>
                              <td className="py-2">
                                <button
                                  onClick={() => handleDownload(item)}
                                  className="cu-l-m text-blue-600 hover:text-blue-800"
                                  title="Scarica"
                                >
                                  <FaDownload size={18} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <Pagination />
                  </div>
                )}
                </div>
              </>
              
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
}